import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const HeroSection = ({ onOpenModal }) => {
  const { t, getCustomMedia } = useLanguage();
  const videoRef = useRef(null);
  
  // Glühende Kohlen Video - Pexels 9251254 (vertikales Video, wird zugeschnitten)
  const defaultVideo = "https://videos.pexels.com/video-files/9251254/9251254-hd_1080_1920_30fps.mp4";
  const heroVideo = getCustomMedia('hero', 'video') || defaultVideo;
  
  // Slow down video playback for cinematic effect
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.5;
      videoRef.current.load();
    }
  }, [heroVideo]);
  
  return (
    <section 
      id="hero"
      className="relative h-screen w-full overflow-hidden bg-[#030303]"
      data-testid="hero-section"
    >
      {/* Video Background - Glühende Kohlen */}
      <div className="absolute inset-0">
        <video
          ref={videoRef}
          key={heroVideo}
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover opacity-50 scale-150"
        >
          <source 
            src={heroVideo} 
            type="video/mp4" 
          />
        </video>
        {/* Mystische Verläufe für sanfte Übergänge */}
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/30 to-black" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-black/50" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_20%,rgba(0,0,0,0.8)_80%)]" />
      </div>
      
      {/* Noise Texture */}
      <div className="noise-overlay" />
      
      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full px-6">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 2.5 }}
          className="text-center"
        >
          {/* Small pre-title */}
          <motion.p 
            className="text-[10px] md:text-xs tracking-[0.5em] uppercase text-[#2bfecd]/60 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.5, delay: 0.5 }}
          >
            Est. MMXXVI • München
          </motion.p>
          
          {/* Logo/Brand */}
          <motion.h1 
            className="font-cinzel text-5xl md:text-8xl lg:text-[10rem] tracking-[0.2em] md:tracking-[0.3em] uppercase text-white font-light"
            initial={{ opacity: 0, y: 50, letterSpacing: '0.4em' }}
            animate={{ opacity: 1, y: 0, letterSpacing: '0.2em' }}
            transition={{ duration: 2, delay: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            style={{ textShadow: '0 0 100px rgba(43, 254, 205, 0.15)' }}
          >
            ICEBACH
          </motion.h1>
          <motion.p
            className="font-cinzel text-2xl md:text-4xl lg:text-5xl tracking-[0.4em] md:tracking-[0.5em] uppercase mb-8 text-white/60 font-light"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.5, delay: 1.2 }}
          >
            SAUNA
          </motion.p>
          
          {/* Decorative Line */}
          <motion.div 
            className="flex items-center justify-center gap-4 mb-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.5, delay: 1.8 }}
          >
            <div className="w-16 md:w-24 h-px bg-gradient-to-r from-transparent to-[#2bfecd]/50" />
            <div className="w-2 h-2 rotate-45 border border-[#2bfecd]/50" />
            <div className="w-16 md:w-24 h-px bg-gradient-to-l from-transparent to-[#2bfecd]/50" />
          </motion.div>
          
          {/* Tagline */}
          <motion.p 
            className="text-xs md:text-sm tracking-[0.4em] uppercase text-[#2bfecd] mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 2.2 }}
            style={{ textShadow: '0 0 30px rgba(43, 254, 205, 0.5)' }}
          >
            {t('hero.tagline')}
          </motion.p>
          
          {/* Subtitle */}
          <motion.p 
            className="text-sm md:text-base tracking-[0.15em] text-white/50 mb-16 max-w-xl mx-auto font-light"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 2.5 }}
          >
            {t('hero.subtitle')}
          </motion.p>
          
          {/* CTA Button */}
          <motion.button
            onClick={onOpenModal}
            className="group relative px-12 py-5 border border-[#2bfecd]/30 text-[#2bfecd] text-xs tracking-[0.3em] uppercase font-cinzel overflow-hidden transition-all duration-700 hover:border-[#2bfecd] hover:shadow-[0_0_50px_rgba(43,254,205,0.3)]"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 3 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="hero-cta-button"
          >
            <span className="relative z-10">{t('hero.cta')}</span>
            <div className="absolute inset-0 bg-[#2bfecd] transform -translate-x-full group-hover:translate-x-0 transition-transform duration-700" />
            <span className="absolute inset-0 flex items-center justify-center text-black opacity-0 group-hover:opacity-100 transition-opacity duration-700 delay-100 z-20 tracking-[0.3em]">
              {t('hero.cta')}
            </span>
          </motion.button>
        </motion.div>
      </div>
      
      {/* Scroll Indicator */}
      <motion.div 
        className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 3.5 }}
      >
        <span className="text-[9px] tracking-[0.4em] uppercase text-white/30">
          {t('hero.scroll')}
        </span>
        <motion.div 
          className="w-px h-16 bg-gradient-to-b from-[#2bfecd]/50 to-transparent"
          animate={{ scaleY: [1, 0.5, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>
    </section>
  );
};

export default HeroSection;
